import { realtimeApi } from "./realtimeApi";
import { Project } from "./projectsApi";

// Real Time can only monitor custom polygons (see MonitoringSetupPage /
// REALTIME_SUPPORTED_TYPES), so "all polygons in a project" means its
// custom_polygon members — the rest can't be monitored and are ignored here.
const REALTIME_SUPPORTED_TYPES = ["custom_polygon"];

export interface AreaLite {
  id: string;
  name: string;
  type: string;
}

export interface MonitorMember {
  id: string;
  name: string;
  monitored: boolean;
}

export interface SharedMember {
  id: string;
  name: string;
  otherProjects: string[];
}

/** The monitorable (custom-polygon) members of a project, with current state. */
export function monitorableMembers(
  areaIds: string[],
  allAreas: AreaLite[],
  monitoredIds: Set<string>
): MonitorMember[] {
  const byId = new Map(allAreas.map((a) => [a.id, a]));
  const out: MonitorMember[] = [];
  for (const id of areaIds) {
    const a = byId.get(id);
    if (a && REALTIME_SUPPORTED_TYPES.includes(a.type)) {
      out.push({ id: a.id, name: a.name, monitored: monitoredIds.has(a.id) });
    }
  }
  return out;
}

/**
 * Of the given members, which also belong to OTHER projects — so a bulk disable
 * can warn that it will stop monitoring in those projects too. Monitoring is a
 * property of the polygon (one `monitored` flag per map_id), shared across every
 * project the polygon belongs to, so disabling it here disables it everywhere.
 */
export function sharedMembers(
  members: MonitorMember[],
  allProjects: Project[],
  exceptProjectId?: string
): SharedMember[] {
  const others = allProjects.filter((p) => p.id !== exceptProjectId);
  const out: SharedMember[] = [];
  for (const m of members) {
    const names = others.filter((p) => p.areaIds.includes(m.id)).map((p) => p.name);
    if (names.length) out.push({ id: m.id, name: m.name, otherProjects: names });
  }
  return out;
}

/** Toggle monitoring for each id to `enable`. Best-effort; returns counts. */
export async function bulkSetMonitoring(
  ids: string[],
  enable: boolean
): Promise<{ changed: number; failed: number }> {
  if (!ids.length) return { changed: 0, failed: 0 };
  const results = await Promise.allSettled(
    ids.map((map_id) => realtimeApi.toggleMonitoring({ map_id, enable }))
  );
  const changed = results.filter((r) => r.status === "fulfilled").length;
  return { changed, failed: results.length - changed };
}
